import type { ReactNode } from "react";

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  /** Horizontal alignment for this column's header and cells. Defaults to "left".
   *  Use "right" for numeric columns so the digits line up on the decimal edge. */
  align?: "left" | "right" | "center";
}

function alignClass(align: Column<unknown>["align"]): string | undefined {
  if (align === "right") return "fui-table__col--right";
  if (align === "center") return "fui-table__col--center";
  return undefined;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  empty?: ReactNode;
}

/** A token-styled list table — the generic primitive every list screen composes. */
export function DataTable<T>({ columns, rows, getRowKey, empty }: DataTableProps<T>) {
  if (rows.length === 0) {
    return <p className="fui-muted">{empty ?? "Geen resultaten."}</p>;
  }
  return (
    <table className="fui-table">
      <thead>
        <tr>
          {columns.map((c, i) => (
            <th key={i} className={alignClass(c.align)}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={getRowKey(row)}>
            {columns.map((c, i) => (
              <td key={i} className={alignClass(c.align)}>
                {c.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
